import { useLayoutEffect } from "react";
import LocalStorageUtils from "@/utils/localStorageUtils";
import { applicant_details } from "@/data/forms/steps/applicant-details";
import { contact_information } from "@/data/forms/steps/contact-information";
import { travelPlansGroup } from "@/data/forms/steps/fields/travel-plans-fields";
import { FormStepType } from "@/data/forms/types";
import { parseStoredRecord, TRAVEL_PLANS_FIELD_NAMES } from "@/utils/debugFormPersist";

const allForms: FormStepType[] = [
  applicant_details,
  contact_information({ additionalPhoneNumbers: 1 }),
];

const getFieldsFromSteps = (steps: FormStepType[]) =>
  steps.flatMap((step) =>
    step.groups.flatMap((group) => [
      ...group.fields.map((field) => field.name),
      ...(group.subGroups?.flatMap((subGroup) => subGroup.fields.map((field) => field.name)) ?? []),
    ])
  );

const travelPlansFields = travelPlansGroup.fields.map((field) => field.name);

export const COMMON_FIELDS = [...getFieldsFromSteps(allForms), ...travelPlansFields];

export const TRAVEL_PLANS_FIELDS = TRAVEL_PLANS_FIELD_NAMES;

const NON_PERSISTABLE_FIELDS = new Set(["addons"]);

const hasPersistableValue = (value: unknown) =>
  value !== undefined && value !== null && value !== "";

export const getPersistedFormValues = (formName: string): Record<string, unknown> => {
  const sharedData = parseStoredRecord("FORM_DATA");
  const specificData = parseStoredRecord(`FORM_DATA_${formName}`);

  return { ...sharedData, ...specificData };
};

export const persistFormValues = (
  value: Record<string, unknown>,
  localStorageKey: string,
  options?: { activeFields?: string[] }
) => {
  if (!localStorageKey) return;

  const existingShared = parseStoredRecord("FORM_DATA");
  const existingSpecific = parseStoredRecord(localStorageKey);

  const sharedData = { ...existingShared };
  const specificData = { ...existingSpecific };
  const activeFields = new Set(options?.activeFields ?? []);

  Object.entries(value).forEach(([key, fieldValue]) => {
    if (fieldValue === undefined || NON_PERSISTABLE_FIELDS.has(key)) return;

    const isActiveField = activeFields.has(key);
    if (!isActiveField && !hasPersistableValue(fieldValue)) return;

    if (COMMON_FIELDS.includes(key)) {
      sharedData[key] = fieldValue;
      return;
    }

    specificData[key] = fieldValue;
  });

  LocalStorageUtils.setItem("FORM_DATA", JSON.stringify(sharedData));
  LocalStorageUtils.setItem(localStorageKey, JSON.stringify(specificData));
};

export const usePersistForm = ({
  value,
  localStorageKey,
  activeFields,
}: {
  value: Record<string, unknown>;
  localStorageKey: string;
  activeFields?: string[];
}) => {
  useLayoutEffect(() => {
    if (!localStorageKey || Object.keys(value).length === 0) return;

    persistFormValues(value, localStorageKey, { activeFields });
  }, [value, localStorageKey, activeFields]);

  return;
};
